import { useEffect, useRef, useState } from "react";

import "maplibre-gl/dist/maplibre-gl.css";

/**
 * Klient-only OpenStreetMap-karta (MapLibre GL) utan API-nyckel.
 * Laddas först när kartan är nära viewporten så att startsidan inte
 * betalar för biblioteket i onödan.
 */
export function OsmMap({
  lat,
  lng,
  zoom = 15,
  className,
  title,
  markerEl,
}: {
  lat: number;
  lng: number;
  zoom?: number;
  className?: string;
  title: string;
  /** DOM-nod som används som markör (renderas av föräldern). */
  markerEl?: HTMLElement | null;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || visible) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible || !containerRef.current) return;
    // Äldre webbläsare saknar WebGL2. Utan kontroll kraschar kartbiblioteket
    // med ett fel i konsolen; i stället visas en länk till kartan.
    if (!supportsWebgl2()) {
      setFailed(true);
      return;
    }
    let map: import("maplibre-gl").Map | undefined;
    let cancelled = false;

    void (async () => {
      try {
        const maplibregl = await import("maplibre-gl");
        if (cancelled || !containerRef.current) return;
        const m = new maplibregl.Map({
          container: containerRef.current,
          // Låt en finger scrolla sidan på mobil; två fingrar panorerar kartan.
          cooperativeGestures: true,
          style: {
            version: 8,
            sources: {
              osm: {
                type: "raster",
                tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
                tileSize: 256,
                maxzoom: 19,
                attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
              },
            },
            layers: [{ id: "osm", type: "raster", source: "osm" }],
          },
          center: [lng, lat],
          zoom,
          attributionControl: { compact: true },
        });
        map = m;
        m.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
        m.scrollZoom?.disable();
        if (markerEl) {
          new maplibregl.Marker({ element: markerEl, anchor: "bottom" })
            .setLngLat([lng, lat])
            .addTo(m);
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      map?.remove();
    };
  }, [visible, lat, lng, zoom, markerEl]);

  return (
    <div ref={containerRef} role="img" aria-label={title} className={className}>
      {failed ? (
        <div className="flex h-full w-full items-center justify-center bg-muted p-4 text-center text-sm">
          <a
            className="font-medium text-primary link-quiet"
            href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=${zoom}/${lat}/${lng}`}
            target="_blank"
            rel="noreferrer"
          >
            Öppna kartan i en ny flik
          </a>
        </div>
      ) : null}
    </div>
  );
}

/** WebGL2 krävs av kartbiblioteket. Kontrolleras en gång per sidladdning. */
let webgl2: boolean | undefined;
function supportsWebgl2(): boolean {
  if (webgl2 !== undefined) return webgl2;
  try {
    const canvas = document.createElement("canvas");
    webgl2 = Boolean(canvas.getContext("webgl2"));
  } catch {
    webgl2 = false;
  }
  return webgl2;
}
